import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n';
import { FiCpu, FiSearch, FiMessageCircle, FiLink, FiHome, FiZap, FiMessageSquare, FiCheck, FiShield, FiMapPin, FiCompass } from 'react-icons/fi';
import { getVisitorStats } from '../api/chat';

const iconMap = {
    cpu: FiCpu,
    search: FiSearch,
    chat: FiMessageCircle,
    link: FiLink,
    home: FiHome,
    zap: FiZap,
    messageSquare: FiMessageSquare,
    check: FiCheck,
    shield: FiShield,
    mapPin: FiMapPin,
    compass: FiCompass
};

const Icon = ({ name, size = 32 }) => {
    const Component = iconMap[name];
    return Component ? <Component size={size} /> : null;
};

const LandingPage = ({ onOpenWidget, onOpenPrivacy }) => {
    const { lang, toggleLanguage } = useLanguage();
    const [visitorCount, setVisitorCount] = useState(124);

    useEffect(() => {
        getVisitorStats().then(data => {
            if (data && data.total_visitors) {
                setVisitorCount(data.total_visitors);
            }
        });
    }, []);

    const content = {
        id: {
            nav: { features: 'Fitur', howItWorks: 'Cara Kerja', embed: 'Untuk Developer', cta: 'Mulai Chat' },
            hero: {
                badge: '✨ Asisten Hunian Berbasis Agentic AI',
                title: 'Temukan Hunian Impianmu dengan Asisten Cerdas AI',
                subtitle: 'Singgah mencarikan kost dan kontrakan terbaik dari berbagai portal sekaligus, disesuaikan dengan budget, jarak komuter, dan analisis banjir — dalam hitungan detik.',
                cta: 'Mulai Cari Sekarang',
                ctaSub: 'Gratis · Langsung Chat · Tanpa Registrasi',
            },
            stats: [
                { value: `${visitorCount}+`, label: 'Pengguna Terdaftar' },
                { value: '5+', label: 'Portal Properti Terintegrasi' },
                { value: 'OSM', label: 'Stitching Rute Peta Lokal' },
                { value: '100%', label: 'Gratis Selamanya' },
            ],
            features: {
                title: 'Mengapa Mencari dengan Singgah?',
                subtitle: 'Satu chat, cari di semua portal properti sekaligus lengkap dengan analitik geospasial.',
                items: [
                    { icon: 'cpu', title: 'Rekomendasi AI Cerdas', desc: 'AI memahami kebutuhanmu — status pernikahan, budget, dan area — lalu merekomendasikan pilihan hunian yang benar-benar pas.' },
                    { icon: 'search', title: 'Multi-Portal Aggregator', desc: 'Mencari otomatis dari Rumah123, Lamudi, Mamikost, 99.co, dan Facebook Marketplace sekaligus.' },
                    { icon: 'shield', title: 'Analisis Risiko Banjir & Tinggi', desc: 'Cek ketinggian daerah (mdpl) lewat Open-Meteo dan analisis historis banjir dari berita internet secara otomatis sebelum memesan.' },
                    { icon: 'mapPin', title: 'Kalkulator Jarak Komuter', desc: 'Hitung jarak tempuh (km) dan durasi perjalanan (menit) ke kantor atau kampusmu, lengkap dengan rute garis peta visual.' },
                    { icon: 'compass', title: 'Pencarian Semantik', desc: 'Sistem memahami variasi kata secara natural. "sewa rumah", "dikontrakan", atau "kamar kos" dianalisis secara semantik.' },
                    { icon: 'zap', title: 'Hubungi via WhatsApp', desc: 'Simpan properti favoritmu dan tim admin kami akan segera membantu koordinasi kunjungan dan kontak pemilik via WhatsApp.' },
                ],
            },
            howItWorks: {
                title: 'Cara Kerja Singgah',
                subtitle: 'Tiga langkah mudah menuju hunian impianmu',
                steps: [
                    { num: '01', icon: 'messageSquare', title: 'Mulai Percakapan', desc: 'Klik tombol chat untuk menyapa asisten virtual Mbok Yem. Tanpa akun, langsung mulai.' },
                    { num: '02', icon: 'cpu', title: 'Tentukan Kriteria', desc: 'Ceritakan lokasi kerja, budget maksimal, dan tipe kamar yang diinginkan seperti mengobrol biasa.' },
                    { num: '03', icon: 'home', title: 'Bandingkan & Booking', desc: 'Lihat peta rute perjalanan, nilai status bebas banjir, dan simpan listing favorit untuk dihubungi.' },
                ],
            },
            embed: {
                title: 'Integrasikan Singgah ke Website Anda',
                subtitle: 'Singgah tersedia sebagai widget yang mudah dipasang. Cukup tambahkan 2 baris kode HTML.',
                tag: 'Untuk Developer & Platform Mitra',
                items: ['Cocok untuk situs portal properti, website developer perumahan, dan platform akomodasi', 'Zero server configuration — widget berjalan di atas infrastruktur cloud Singgah', 'Desain responsif, modern, dan mendukung multi-bahasa secara default'],
                ctaLabel: 'Hubungi Tim Developer',
            },
            footer: {
                tagline: 'Asisten hunian cerdas untuk Indonesia.',
                links: ['Kebijakan Privasi', 'Tentang Kami', 'Kontak'],
                copy: '© 2026 Singgah SmartAdvisor. All rights reserved.',
            },
        },
        en: {
            nav: { features: 'Features', howItWorks: 'How It Works', embed: 'For Developers', cta: 'Start Chat' },
            hero: {
                badge: '✨ Agentic AI Housing Assistant',
                title: 'Find Your Ideal Home with AI Assistance',
                subtitle: 'Singgah searches for the best boarding houses and rentals from multiple portals simultaneously, tailored to your budget, commute distance, and flood analysis — in seconds.',
                cta: 'Start Searching Now',
                ctaSub: 'Free · Chat directly · No registration required',
            },
            stats: [
                { value: `${visitorCount}+`, label: 'Registered Users' },
                { value: '5+', label: 'Integrated Portals' },
                { value: 'OSM', label: 'Local Map Route Stitching' },
                { value: '100%', label: 'Free Forever' },
            ],
            features: {
                title: 'Why Find with Singgah?',
                subtitle: 'One chat, search all property portals at once with geolocational analysis.',
                items: [
                    { icon: 'cpu', title: 'Smart AI Matching', desc: 'The AI understands your goals — marital status, budget, and work area — and recommends matches that fit.' },
                    { icon: 'search', title: 'Multi-Portal Aggregator', desc: 'Queries listings from Rumah123, Lamudi, Mamikost, 99.co, and Facebook Marketplace concurrently.' },
                    { icon: 'shield', title: 'Flood Risk & Elevation Check', desc: 'Instantly view altitude (above sea level) via Open-Meteo and review local flood reports before committing.' },
                    { icon: 'mapPin', title: 'Live Commute Calculator', desc: 'Check distance (km) and travel duration (minutes) to your workplace or campus, complete with route map drawings.' },
                    { icon: 'compass', title: 'Semantic Keyword Search', desc: 'The system resolves keyword overlaps like "sewa", "dikontrakan", or "kosan" to matching categories.' },
                    { icon: 'zap', title: 'WhatsApp Direct Save', desc: 'Bookmark properties, and our admin team will coordinate bookings and reach out to owners via WhatsApp.' },
                ],
            },
            howItWorks: {
                title: 'How Singgah Works',
                subtitle: 'Three simple steps to your dream home',
                steps: [
                    { num: '01', icon: 'messageSquare', title: 'Start Chatting', desc: 'Click the chat launcher to meet our virtual agent Mbok Yem. No account required.' },
                    { num: '02', icon: 'cpu', title: 'Describe Your Criteria', desc: 'Chat naturally about your work location, maximum budget, and preferred style.' },
                    { num: '03', icon: 'home', title: 'Compare & Book', desc: 'Browse a curated list of properties with photos, prices, and direct links to the sources.' },
                ],
            },
            embed: {
                title: 'Integrate Singgah into Your Site',
                subtitle: 'Singgah is available as an embeddable widget. Simply paste 2 lines of HTML code.',
                tag: 'For Developers & Portal Partners',
                items: ['Perfect for real estate search portals, developer sites, and accommodation listings', 'No additional server needed — runs on Singgah infrastructure', 'Responsive layout, lightweight code, and bilingual translation support'],
                ctaLabel: 'Contact Dev Team',
            },
            footer: {
                tagline: 'Smart housing assistant for Indonesia.',
                links: ['Privacy Policy', 'About Us', 'Contact'],
                copy: '© 2026 Singgah SmartAdvisor. All rights reserved.',
            },
        },
    };

    const c = content[lang] || content.id;

    return (
        <div style={{ fontFamily: 'Rubik, Arial, sans-serif', background: '#fff', minHeight: '100vh', overflowX: 'hidden' }}>

            {/* NAVBAR */}
            <nav style={{
                position: 'sticky', top: 0, zIndex: 100,
                background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)',
                borderBottom: '1px solid #f0fdf4',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 24px', height: 68,
                boxShadow: '0 1px 4px rgba(0,0,0,0.02)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src="avatar.jpg" alt="Singgah" style={{ height: 44, width: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2px solid #16a34a' }} />
                    <span style={{ fontWeight: 800, fontSize: 22, color: '#16a34a', letterSpacing: '-0.5px' }}>Singgah</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <button
                        onClick={toggleLanguage}
                        data-test-id="landing-language-toggle"
                        className="button-hover-effect"
                        style={{ background: 'none', border: '1.5px solid #16a34a', borderRadius: 20, padding: '4px 14px', cursor: 'pointer', fontSize: 12, color: '#16a34a', fontWeight: 700 }}
                    >
                        {lang === 'id' ? 'ENGLISH' : 'INDONESIA'}
                    </button>
                    <button onClick={onOpenWidget} data-test-id="landing-nav-cta" className="button-hover-effect" style={navCtaStyle}>{c.nav.cta}</button>
                </div>
            </nav>

            {/* HERO */}
            <section style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #e6fcf0 35%, #ffffff 85%)',
                padding: '90px 24px 70px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
            }}>
                <div style={{ position: 'absolute', top: -60, right: -60, width: 300, height: 300, background: 'radial-gradient(circle, rgba(22,163,74,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', bottom: -40, left: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(22,163,74,0.06) 0%, transparent 70%)', borderRadius: '50%' }} />

                <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative' }} className="animate-slide-up">
                    <div style={{ display: 'inline-block', background: '#dcfce7', color: '#15803d', borderRadius: 20, padding: '6px 18px', fontSize: 11, fontWeight: 700, marginBottom: 20, border: '1px solid #bbf7d0' }}>
                        {c.hero.badge}
                    </div>
                    <h1 style={{ fontSize: 'clamp(28px, 4.8vw, 50px)', fontWeight: 800, color: '#111827', lineHeight: 1.25, marginBottom: 24, letterSpacing: '-0.5px' }}>
                        {c.hero.title.split(' ').map((word, i) =>
                            ['AI', 'AI,', 'AI-Powered', 'Cerdas'].includes(word)
                                ? <span key={i} style={{ color: '#16a34a' }}>{word} </span>
                                : word + ' '
                        )}
                    </h1>
                    <p style={{ fontSize: 'clamp(14px, 2.2vw, 17px)', color: '#4b5563', lineHeight: 1.75, marginBottom: 36, maxWidth: 640, margin: '0 auto 36px' }}>
                        {c.hero.subtitle}
                    </p>
                    <button onClick={onOpenWidget} data-test-id="landing-hero-cta" className="button-hover-effect" style={heroCta}>
                        {c.hero.cta} &nbsp;➜
                    </button>
                    <div style={{ marginTop: 14, fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{c.hero.ctaSub}</div>
                </div>

                <div className="animate-slide-up" style={{ maxWidth: 740, margin: '54px auto 0', borderRadius: 24, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb' }}>
                    <div style={{ background: '#f8fafc', padding: '12px 20px', display: 'flex', gap: 6, borderBottom: '1px solid #e2e8f0', alignItems: 'center' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#eab308' }} />
                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                        <div style={{ flex: 1, backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, height: 24, margin: '0 12px', fontSize: 11, display: 'flex', alignItems: 'center', paddingLeft: 10, color: '#94a3b8' }}>
                            singgah-dashboard.app
                        </div>
                    </div>
                    <img src="bgDashboard.svg" alt="Singgah Preview" style={{ width: '100%', display: 'block' }} onError={e => { const target = e.currentTarget || e.target; if (target && target.style) target.style.display = 'none'; }} />
                    <div style={{ background: '#f0fdf4', padding: '18px 24px', borderTop: '1px solid #dcfce7' }}>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {['Rumah123', 'Pinhome', 'Lamudi', 'Mamikost', '99.co'].map(s => (
                                <span key={s} style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: 20, padding: '4px 14px', fontSize: 11, color: '#15803d', fontWeight: 600 }}>{s}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section style={{ background: '#16a34a', padding: '48px 24px', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 28, textAlign: 'center' }}>
                    {c.stats.map((s, i) => (
                        <div key={i} className="animate-fade-in">
                            <div style={{ fontSize: 34, fontWeight: 800, color: '#fff', letterSpacing: '-1px' }}>{s.value}</div>
                            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" style={{ padding: '90px 24px', background: '#fff' }}>
                <div style={{ maxWidth: 960, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 58 }}>
                        <h2 style={{ fontSize: 'clamp(24px, 4.2vw, 38px)', fontWeight: 800, color: '#111827', marginBottom: 14, letterSpacing: '-0.5px' }}>{c.features.title}</h2>
                        <p style={{ fontSize: 15, color: '#6b7280', maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>{c.features.subtitle}</p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                        {c.features.items.map((f, i) => (
                            <div key={i} className="property-card-hover" style={{
                                background: '#f9fafb', borderRadius: 20, padding: 28,
                                border: '1px solid #f3f4f6',
                                display: 'flex', flexDirection: 'column'
                            }}>
                                <div style={{ width: 56, height: 56, borderRadius: 14, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a', marginBottom: 20 }}>
                                    <Icon name={f.icon} size={28} />
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 10, letterSpacing: '-0.25px' }}>{f.title}</div>
                                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.65 }}>{f.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how-it-works" style={{ padding: '90px 24px', background: '#f0fdf4', borderTop: '1px solid #e6fcf0' }}>
                <div style={{ maxWidth: 880, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 58 }}>
                        <h2 style={{ fontSize: 'clamp(24px, 4.2vw, 38px)', fontWeight: 800, color: '#111827', marginBottom: 14, letterSpacing: '-0.5px' }}>{c.howItWorks.title}</h2>
                        <p style={{ fontSize: 15, color: '#6b7280', fontWeight: 500 }}>{c.howItWorks.subtitle}</p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 36 }}>
                        {c.howItWorks.steps.map((s, i) => (
                            <div key={i} style={{ textAlign: 'center', position: 'relative' }}>
                                <div style={{ width: 88, height: 88, background: '#16a34a', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#fff', boxShadow: '0 4px 14px rgba(22,163,74,0.25)' }}>
                                    <Icon name={s.icon} size={38} />
                                </div>
                                <div style={{ position: 'absolute', top: 24, left: '60%', width: 'calc(40% + 20px)', height: 2, background: '#bbf7d0', display: i < 2 ? 'block' : 'none' }} />
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 8, letterSpacing: '1px' }}>LANGKAH {s.num}</div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: '#111827', marginBottom: 10 }}>{s.title}</div>
                                <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.65 }}>{s.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EMBED / DEVELOPER */}
            <section id="embed" style={{ padding: '90px 24px', background: '#fff' }}>
                <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 48, alignItems: 'center' }}>
                    <div>
                        <div style={{ display: 'inline-block', background: '#dcfce7', color: '#15803d', borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700, marginBottom: 16 }}>
                            {c.embed.tag}
                        </div>
                        <h2 style={{ fontSize: 'clamp(22px, 3.8vw, 34px)', fontWeight: 800, color: '#111827', marginBottom: 18, letterSpacing: '-0.5px' }}>{c.embed.title}</h2>
                        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 24 }}>{c.embed.subtitle}</p>
                        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px' }}>
                            {c.embed.items.map((item, i) => (
                                <li key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                                    <span style={{ color: '#16a34a', fontWeight: 700, flexShrink: 0 }}><Icon name="check" size={13} /></span> {item}
                                </li>
                            ))}
                        </ul>
                        <button data-test-id="landing-embed-cta" className="button-hover-effect" style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 12px rgba(22,163,74,0.2)' }}>
                            {c.embed.ctaLabel}
                        </button>
                    </div>
                    <div style={{ background: '#0f172a', borderRadius: 20, padding: 28, fontFamily: 'monospace', boxShadow: '0 20px 40px rgba(15,23,42,0.15)', border: '1px solid #1e293b' }}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14 }}>HTML · 2 Lines Integration</div>
                        <div style={{ fontSize: 12, lineHeight: 1.85, overflowX: 'auto', color: '#e2e8f0' }}>
                            <span style={{ color: '#94a3b8' }}>&lt;</span>
                            <span style={{ color: '#7dd3fc' }}>script</span>
                            <span style={{ color: '#94a3b8' }}> src=</span>
                            <span style={{ color: '#86efac' }}>"https://singgah.app/widget.js"</span>
                            <span style={{ color: '#94a3b8' }}>&gt;&lt;/</span>
                            <span style={{ color: '#7dd3fc' }}>script</span>
                            <span style={{ color: '#94a3b8' }}>&gt;</span>
                            <br />
                            <span style={{ color: '#94a3b8' }}>&lt;</span>
                            <span style={{ color: '#7dd3fc' }}>div</span>
                            <span style={{ color: '#94a3b8' }}> id=</span>
                            <span style={{ color: '#86efac' }}>"singgah-widget"</span>
                            <span style={{ color: '#fbbf24' }}> data-lang=</span>
                            <span style={{ color: '#86efac' }}>"id"</span>
                            <span style={{ color: '#94a3b8' }}>&gt;&lt;/</span>
                            <span style={{ color: '#7dd3fc' }}>div</span>
                            <span style={{ color: '#94a3b8' }}>&gt;</span>
                        </div>
                        <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid #1e293b', fontSize: 11, color: '#64748b', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <span>✓ Zero Dependency</span>
                            <span>✓ Responsive Width</span>
                            <span>✓ Auto Bi-lingual</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA BANNER */}
            <section style={{ background: 'linear-gradient(135deg, #16a34a 0%, #0c7a36 100%)', padding: '72px 24px', textAlign: 'center' }}>
                <h2 style={{ fontSize: 'clamp(24px, 4.2vw, 38px)', fontWeight: 800, color: '#fff', marginBottom: 18, letterSpacing: '-0.5px' }}>
                    {lang === 'id' ? 'Siap Temukan Tempat Singgah Terbaikmu?' : 'Ready to Discover Your Best Place?'}
                </h2>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', marginBottom: 36, maxWidth: 540, margin: '0 auto 36px', lineHeight: 1.6 }}>
                    {lang === 'id' ? 'Mulai chat interaktif dengan Mbok Yem sekarang. Cepat, akurat, dan 100% gratis langsung dari browser Anda.' : 'Chat with Mbok Yem now. Fast, accurate, and 100% free directly from your browser.'}
                </p>
                <button onClick={onOpenWidget} data-test-id="landing-bottom-cta" className="button-hover-effect" style={{ background: '#fff', color: '#16a34a', border: 'none', borderRadius: 12, padding: '15px 36px', fontWeight: 800, fontSize: 16, cursor: 'pointer', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}>
                    {lang === 'id' ? 'Mulai Percakapan ➜' : 'Start Chatting ➜'}
                </button>
            </section>

            {/* FOOTER */}
            <footer style={{ background: '#0f172a', padding: '48px 24px', color: '#94a3b8', borderTop: '1px solid #1e293b' }}>
                <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <img src="avatar.jpg" alt="Singgah" style={{ height: 40, width: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2px solid #334155' }} />
                            <span style={{ fontWeight: 800, fontSize: 18, color: '#fff', letterSpacing: '-0.25px' }}>Singgah</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{c.footer.tagline}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
                        {c.footer.links.map((l, i) => {
                            const isContact = l === 'Kontak' || l === 'Contact';
                            const isPrivacy = l === 'Kebijakan Privasi' || l === 'Privacy Policy';
                            if (isContact) {
                                return (
                                    <a key={i} href="https://wa.me/6282276648478" target="_blank" rel="noopener noreferrer" style={footerLinkBtn} data-test-id="landing-footer-whatsapp-link">
                                        {l}
                                    </a>
                                );
                            }
                            return (
                                <button key={i} type="button" className="button-hover-effect" style={footerLinkBtn} onClick={() => isPrivacy ? onOpenPrivacy() : onOpenWidget()} data-test-id={`landing-footer-link-${i}`}>
                                    {l}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div style={{ maxWidth: 900, margin: '24px auto 0', borderTop: '1px solid #1e293b', paddingTop: 24, fontSize: 11, color: '#475569', textAlign: 'center' }}>
                    {c.footer.copy}
                </div>
            </footer>
        </div>
    );
};

const navLinkStyle = { fontSize: 14, color: '#374151', textDecoration: 'none', fontWeight: 500 };
const footerLinkBtn = { fontSize: 12, color: '#94a3b8', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500 };
const navCtaStyle = {
    background: '#16a34a', color: '#fff', border: 'none', borderRadius: 20,
    padding: '8px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(22,163,74,0.2)'
};
const heroCta = {
    background: '#16a34a', color: '#fff', border: 'none', borderRadius: 14,
    padding: '16px 36px', fontWeight: 700, fontSize: 16, cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(22,163,74,0.35)',
};

export default LandingPage;
